import React, { useMemo, useState } from "react";
import {
  Badge,
  Flex,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Skeleton,
  Alert,
  Center,
} from "@mantine/core";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { IconSearch, IconUsers, IconBuilding, IconAlertCircle } from "@tabler/icons-react";
import css2 from "./../../../Insights/pages/MeetingTime/AsAttendee/AsAttendee.module.scss";
import UserManagementDrawer from "./components/UserManagementDrawer";
import useOrgDepartments from "hooks/organizations/useOrgDepartments";
import { useUser } from "stores/useUser";

const DepartmentCard = ({ name, count, onClick, isLoading }) => {
  if (isLoading) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Flex align="center" justify="space-between">
          <Group gap="sm">
            <Skeleton height={20} width={20} />
            <Stack gap={4}>
              <Skeleton height={20} width={120} />
              <Skeleton height={16} width={80} />
            </Stack>
          </Group>
          <Skeleton height={24} width={60} />
        </Flex>
      </Paper>
    );
  }

  return (
    <Paper
      withBorder
      radius="md"
      p="lg"
      style={{ cursor: "pointer" }}
      onClick={onClick}
    >
      <Flex align="center" justify="space-between">
        <Group gap="sm">
          <IconBuilding size={20} style={{ marginBottom: "auto" }} />
          <Stack gap={4}>
            <Title order={5}>{name}</Title>
            <Text size="sm" c="dimmed">
              Department
            </Text>
          </Stack>
        </Group>
        <Badge size="lg" variant="light" color="blue">
          {count} {count === 1 ? "User" : "Users"}
        </Badge>
      </Flex>
    </Paper>
  );
};

const ManageUsers = () => {
  const [query, setQuery] = useState("");
  const [parent] = useAutoAnimate();
  const [drawerState, setDrawerState] = useState({
    opened: false,
    departmentName: "",
    departmentId: null,
  });
  const { user } = useUser();
  const {
    data: departmentsData,
    isLoading,
    isError,
  } = useOrgDepartments(user.subscription.organization_id);
  
  const filteredDepartments = useMemo(() => {
    if (!departmentsData) return [];
    
    if (!query) return departmentsData;
    const q = query.toLowerCase();
    return departmentsData.filter((d) => d.name.toLowerCase().includes(q));
  }, [query, departmentsData]);

  const handleDepartmentClick = (department) => {
    setDrawerState({
      opened: true,
      departmentName: department.name,
      departmentId: department.name, // Using name as ID since API doesn't provide separate ID
    });
  };

  const closeDrawer = () => {
    setDrawerState({
      opened: false,
      departmentName: "",
      departmentId: null,
    });
  };

  return (
    <div className={css2.container}>
      <Stack w={"100%"} py={30}>
        <Flex justify="space-between" align="center" w={"100%"}>
          <Group gap="sm">
            <IconUsers size={24} />
            <Title order={3}>Manage Users</Title>
          </Group>
          <Group gap="sm">
            <TextInput
              placeholder="Search departments"
              icon={<IconSearch size={16} />}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </Group>
        </Flex>

        <SimpleGrid
          cols={4}
          spacing="lg"
          mt={20}
          breakpoints={[
            { maxWidth: 1400, cols: 3, spacing: "md" },
            { maxWidth: 1000, cols: 2, spacing: "md" },
            { maxWidth: 700, cols: 1, spacing: "sm" },
          ]}
          ref={parent}
        >
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 8 }).map((_, index) => (
              <DepartmentCard
                key={`skeleton-${index}`}
                isLoading={true}
              />
            ))
          ) : isError ? (
            // Error state
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error Loading Departments"
              color="red"
              variant="light"
              style={{ gridColumn: "1 / -1" }}
            >
              Failed to load departments. Please try again later.
            </Alert>
          ) : (
            // Success state
            filteredDepartments.map((dept) => (
              <DepartmentCard
                key={dept.name}
                name={dept.name}
                count={dept.users}
                onClick={() => handleDepartmentClick(dept)}
              />
            ))
          )}
        </SimpleGrid>

        {!isLoading && !isError && filteredDepartments.length === 0 && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <IconBuilding size={48} color="var(--mantine-color-gray-4)" />
              <Text c="dimmed" ta="center">
                {query ? "No departments found matching your search." : "No departments available."}
              </Text>
            </Stack>
          </Center>
        )}
      </Stack>

      <UserManagementDrawer
        opened={drawerState.opened}
        onClose={closeDrawer}
        departmentName={drawerState.departmentName}
        departmentId={drawerState.departmentId}
        orgId={user.subscription.organization_id}
      />
    </div>
  );
};

export default ManageUsers;
